import React, { createContext, useContext, ReactNode, useEffect } from "react";
import ReactDOM from "react-dom";
import UI from "@mile-meeting/mile-ui";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { atom, useRecoilState } from "recoil";

type ToastType = "success" | "error" | "warning" | "info";

const toastState = atom<{ text: string; type: ToastType }>({
  key: "toastState",
  default: { text: "", type: "success" },
});

interface ToastContextType {
  showToast: (text: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toast, setToast] = useRecoilState(toastState);
  const location = useLocation();

  const showToast = (text: string, type: ToastType = "success") => {
    setToast({ text, type });
  };

  useEffect(() => {
    if (toast.text) {
      const timer = setTimeout(() => {
        setToast({ text: "", type: "success" });
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [toast, setToast]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toast.text && ReactDOM.createPortal(<ToastMessage text={toast.text} type={toast.type} />, document.body)}
    </ToastContext.Provider>
  );
};

interface ToastMessageProps {
  text: string;
  type: ToastType;
}

const ToastMessage: React.FC<ToastMessageProps> = ({ text, type }) => {
  const [toast, setToast] = useRecoilState(toastState);
  return (
    <UI.Toast
      onCancel={() => setToast({ text: "", type: "success" })}
      type={type}
      text={text}
      open={true}
      styles={{
        position: "fixed",
        zIndex: 9999,
        top: "60px",
        left: "50%",
        transform: "translateX(-50%)",
      }}
    />
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("TostProvider를 찾을 수 없습니다.");
  }
  return context;
};
